import React, { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "../../../../components/ui/Modal";
import { Button } from "../../../../components/ui/Button";
import { FieldLabel } from "../../../../components/ui/FieldLabel";
import { Input } from "../../../../components/ui/Input";
import { createApiCredential } from "../../../../api/integrations";

/** Mismo tope que valida el backend (CreateApiCredentialDto). */
export const MAX_NAME_LENGTH = 60;

/**
 * Alta de una credencial de la API pública.
 *
 * Solo pide el nombre. Los scopes se otorgan todos: hoy la API tiene un único
 * endpoint, y elegir permisos que todavía no hacen nada confundiría.
 *
 * No muestra el secreto: se lo entrega a `onCreated`, que lo pasa a
 * SecretRevealModal. Así el secreto vive en pantalla en un solo lugar.
 */
export default function CreateApiCredentialModal({
  open,
  onClose,
  onCreated,
  maxActive,
  createCredential = createApiCredential,
}) {
  const { t } = useTranslation();
  const formId = useId();
  const nameId = useId();
  const inputRef = useRef(null);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!open) {
      setName("");
      setErrorMessage("");
      setSaving(false);
    }
  }, [open]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving) return;

    const trimmed = name.trim();
    if (!trimmed) {
      setErrorMessage(t("Escribe un nombre para la credencial."));
      inputRef.current?.focus();
      return;
    }
    if (trimmed.length > MAX_NAME_LENGTH) {
      setErrorMessage(
        t("El nombre puede tener hasta {{max}} caracteres.", { max: MAX_NAME_LENGTH }),
      );
      return;
    }

    setSaving(true);
    setErrorMessage("");
    const result = await createCredential({ name: trimmed });
    setSaving(false);

    if ("error" in result || !result.data?.clientSecret) {
      const status = result.status ?? result.error?.statusCode;
      if (status === 409) {
        setErrorMessage(
          t("Ya tienes {{max}} credenciales activas. Revoca una antes de crear otra.", {
            max: maxActive,
          }),
        );
      } else if (status === 401 || status === 403) {
        setErrorMessage(t("No tienes permiso para administrar credenciales de API."));
      } else {
        setErrorMessage(t("No se pudo crear la credencial. Intenta nuevamente."));
      }
      return;
    }

    onCreated(result.data);
  };

  return (
    <Modal
      open={open}
      onClose={saving ? undefined : onClose}
      size="md"
      title={t("Nueva credencial de API")}
      description={t("Ponle un nombre que te ayude a reconocer qué sistema la usa.")}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t("Cancelar")}
          </Button>
          <Button variant="primary" type="submit" form={formId} loading={saving}>
            {t("Crear")}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} className="space-y-1.5" noValidate>
        <FieldLabel htmlFor={nameId}>{t("Nombre de la credencial")}</FieldLabel>
        <Input
          id={nameId}
          ref={inputRef}
          value={name}
          maxLength={MAX_NAME_LENGTH}
          placeholder={t("Por ejemplo: ERP, Tienda Magento")}
          invalid={Boolean(errorMessage)}
          onChange={(event) => setName(event.target.value)}
          autoComplete="off"
        />
        {errorMessage && (
          <p className="text-sm text-status-error" role="alert">
            {errorMessage}
          </p>
        )}
      </form>
    </Modal>
  );
}
